import Link from 'next/link';
import {
  ArrowRight,
  Globe,
  MessageCircleMore,
  PackagePlus,
  ShoppingBag,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UpgradeWallKind } from '@/lib/upgrade-wall';
import { WebOnlyBillingAction } from './web-only-billing-action';

const COPY: Record<UpgradeWallKind, { icon: LucideIcon; title: string; description: string; cta: string }> = {
  items: {
    icon: PackagePlus,
    title: 'Room for more items on Plus',
    description: 'Free includes 20 items. Plus has room for 150 and takes orders.',
    cta: 'See Plus',
  },
  domain: {
    icon: Globe,
    title: 'Use your own domain on Plus',
    description: 'Connect a domain you own while keeping your Sweetspot page and catalog in sync.',
    cta: 'See Plus',
  },
  footer: {
    icon: Sparkles,
    title: 'Remove the footer on Plus',
    description: 'Free pages keep the Sweetspot credit. Plus lets your own brand stand alone.',
    cta: 'See Plus',
  },
  edits: {
    icon: MessageCircleMore,
    title: 'Keep editing with Plus',
    description: "You've used this month's 10 Free edits. Plus includes 100 edits each month.",
    cta: 'See Plus',
  },
  ordering: {
    icon: ShoppingBag,
    title: 'Take orders on Plus',
    description: 'Take pickup orders on your page for a 3% Sweetspot fee. Pro orders are commission-free.',
    cta: 'See Plus',
  },
  generic: {
    icon: Sparkles,
    title: 'This is included on Plus',
    description: 'Upgrade when you want more room and tools. Your page stays live if you remain on Free.',
    cta: 'See plans',
  },
};

export function UpgradeCard({
  kind,
  compact = false,
  description,
  onNavigate,
  className,
}: {
  kind: UpgradeWallKind;
  compact?: boolean;
  description?: string;
  onNavigate?: () => void;
  className?: string;
}) {
  const copy = COPY[kind];
  const Icon = copy.icon;

  return (
    <div
      data-upgrade-kind={kind}
      className={cn(
        'rounded-xl border border-primary/20 bg-primary/5',
        compact ? 'p-3' : 'p-5',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-primary/10',
            compact ? 'h-8 w-8' : 'h-10 w-10',
          )}
        >
          <Icon className={cn('text-primary', compact ? 'h-4 w-4' : 'h-5 w-5')} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={cn('font-semibold', compact ? 'text-sm' : 'text-base')}>{copy.title}</p>
          <p className={cn('text-muted-foreground', compact ? 'mt-0.5 text-xs leading-5' : 'mt-1 text-sm leading-6')}>
            {description || copy.description}
          </p>
          <WebOnlyBillingAction>
            <Link
              href="/dashboard/settings/billing"
              onClick={onNavigate}
              className={cn(
                'mt-2 inline-flex items-center gap-1 font-semibold text-primary underline-offset-4 hover:underline',
                compact ? 'text-xs' : 'text-sm',
              )}
            >
              {copy.cta} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </WebOnlyBillingAction>
        </div>
      </div>
    </div>
  );
}
